import React from 'react';
import { Card, CardActionArea, Typography, Chip, Box, Stack, IconButton } from '@mui/material';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import HotelIcon from '@mui/icons-material/Hotel';
import AttractionsIcon from '@mui/icons-material/Attractions';
import HikingIcon from '@mui/icons-material/Hiking';
import CategoryIcon from '@mui/icons-material/Category';
import PlaceIcon from '@mui/icons-material/Place';
import LinkIcon from '@mui/icons-material/Link';
import FavoriteIcon from '@mui/icons-material/Favorite';
import StarRating from './StarRating';
import config from '../config';

const CATEGORIES = {
  restaurant: { label: 'Restaurant', icon: RestaurantIcon, color: '#e91e63' },
  hotel: { label: 'Hotel', icon: HotelIcon, color: '#9c27b0' },
  attraction: { label: 'Attraction', icon: AttractionsIcon, color: '#2196f3' },
  activity: { label: 'Activity', icon: HikingIcon, color: '#4caf50' },
  other: { label: 'Other', icon: CategoryIcon, color: '#ff9800' },
};

function getMapThumbnail(lat, lng, color) {
  const hex = color.replace('#', '');
  return `https://api.mapbox.com/styles/v1/mapbox/streets-v12/static/pin-l+${hex}(${lng},${lat})/${lng},${lat},13,0/600x200@2x?access_token=${config.mapboxToken}`;
}

export default function ActivityCard({ activity, onClick, onVote, currentUserId }) {
  const cat = CATEGORIES[activity.category] || CATEGORIES.other;
  const Icon = cat.icon;
  const hasLocation = activity.location?.lat && activity.location?.lng;
  const hasPhoto = activity.photoUrls?.length > 0;

  const avgRating =
    activity.ratings?.length > 0
      ? activity.ratings.reduce((s, r) => s + r.stars, 0) / activity.ratings.length
      : 0;

  const voteCount = activity.votes?.length || 0;
  const hasVoted = currentUserId && activity.votes?.some((v) => v.userId === currentUserId);

  const heroSrc = hasPhoto
    ? activity.photoUrls[0]
    : hasLocation
    ? getMapThumbnail(activity.location.lat, activity.location.lng, cat.color)
    : null;

  return (
    <Card
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 6px 24px rgba(0,0,0,0.15)' },
      }}
    >
      <CardActionArea onClick={onClick}>
        {/* Hero: map thumbnail or photo */}
        {heroSrc ? (
          <Box sx={{ position: 'relative', height: 140 }}>
            <Box
              component="img"
              src={heroSrc}
              alt={activity.name}
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
            <Box
              sx={{
                position: 'absolute', inset: 0,
                background: 'linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 60%)',
              }}
            />
            {/* Category chip over image */}
            <Chip
              icon={<Icon style={{ fontSize: 13, color: 'white' }} />}
              label={cat.label}
              size="small"
              sx={{
                position: 'absolute', top: 10, right: 10,
                bgcolor: cat.color, color: 'white', fontWeight: 600, fontSize: 11,
              }}
            />
            {/* Rating over image */}
            {avgRating > 0 && (
              <Box sx={{ position: 'absolute', bottom: 8, right: 10 }}>
                <StarRating value={Math.round(avgRating)} size="small" />
              </Box>
            )}
          </Box>
        ) : (
          /* No image: color accent bar */
          <Box sx={{ height: 6, bgcolor: cat.color }} />
        )}

        {/* Content */}
        <Box sx={{ p: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
            <Typography variant="subtitle1" fontWeight="bold" sx={{ lineHeight: 1.3 }}>
              {activity.name}
            </Typography>
            {!heroSrc && (
              <Chip
                icon={<Icon style={{ fontSize: 12, color: 'white' }} />}
                label={cat.label}
                size="small"
                sx={{ bgcolor: cat.color, color: 'white', fontSize: 11, ml: 1 }}
              />
            )}
          </Box>

          {activity.location?.address && (
            <Stack direction="row" alignItems="center" spacing={0.5} mt={0.5}>
              <PlaceIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
              <Typography variant="caption" color="text.secondary" noWrap>
                {activity.location.name || activity.location.address}
              </Typography>
              {activity.websiteUrl && (
                <LinkIcon sx={{ fontSize: 13, color: 'text.disabled', ml: 0.5 }} />
              )}
            </Stack>
          )}
          {!activity.location?.address && activity.websiteUrl && (
            <Stack direction="row" alignItems="center" spacing={0.5} mt={0.5}>
              <LinkIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
              <Typography variant="caption" color="text.secondary" noWrap>
                Website
              </Typography>
            </Stack>
          )}

          <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
            {!heroSrc || avgRating === 0 ? (
              <StarRating value={Math.round(avgRating)} size="small" />
            ) : <Box />}
            <Typography variant="caption" color="text.disabled">
              {activity.ratings?.length || 0} rating{activity.ratings?.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
        </Box>
      </CardActionArea>

      {/* Vote button */}
      {onVote && (
        <Box display="flex" alignItems="center" px={2} pb={activity.reactions?.length > 0 ? 0.5 : 1}>
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); onVote(); }}
            sx={{ color: hasVoted ? 'error.main' : 'text.disabled' }}
          >
            <FavoriteIcon fontSize="small" />
          </IconButton>
          <Typography variant="caption" color="text.secondary">
            {voteCount}
          </Typography>
        </Box>
      )}

      {/* Emoji reactions */}
      {activity.reactions?.length > 0 && (
        <Box display="flex" flexWrap="wrap" gap={0.5} px={2} pb={1}>
          {activity.reactions.map((r) => (
            <Chip
              key={r.emoji}
              label={`${r.emoji} ${r.userIds.length}`}
              size="small"
              variant="outlined"
              sx={{ fontSize: 11, height: 22 }}
            />
          ))}
        </Box>
      )}
    </Card>
  );
}
